// src/components/Templates/TemplateVariableForm.jsx
/**
 * Formulário para mapeamento de variáveis de template.
 * Permite mapear variáveis do template para campos do lead ou valores fixos.
 */

import React, { useState, useEffect, useCallback } from "react";
import "./Templates.css";

// Campos disponíveis do lead para mapeamento
const LEAD_FIELDS = [
  { key: "name", label: "Nome", hint: "Nome do lead" },
  { key: "email", label: "Email", hint: "Email do lead" },
  { key: "phone", label: "Telefone", hint: "Telefone do lead" },
  { key: "company", label: "Empresa", hint: "Empresa do lead" },
];

// Detecção automática baseada em hints e nomes de variáveis
const autoDetectField = (variableKey, variableHint) => {
  const keyLower = String(variableKey).toLowerCase();
  const hintLower = (variableHint || "").toLowerCase();

  // Verificar hints primeiro
  if (hintLower.includes("nome") || hintLower.includes("name")) {
    return { type: "lead_field", field: "name" };
  }
  if (hintLower.includes("email")) {
    return { type: "lead_field", field: "email" };
  }
  if (hintLower.includes("telefone") || hintLower.includes("phone")) {
    return { type: "lead_field", field: "phone" };
  }
  if (hintLower.includes("empresa") || hintLower.includes("company")) {
    return { type: "lead_field", field: "company" };
  }

  // Se é {{1}}, {{2}}, etc., tentar inferir pela posição comum
  if (keyLower === "1") {
    return { type: "lead_field", field: "name" };
  }

  // Default: campo fixo vazio
  return { type: "fixed", value: "" };
};

const TemplateVariableForm = ({
  template,
  initialMapping = {},
  onChange,
  leads = [],
  showPreview = true,
}) => {
  const [mapping, setMapping] = useState({});

  // Inicializar mapeamento quando template mudar
  useEffect(() => {
    if (!template?.variables || template.variables.length === 0) {
      setMapping({});
      return;
    }

    const newMapping = {};
    template.variables.forEach((variable) => {
      const key = variable.key || variable;
      const hint = variable.hint || "";

      // Usar mapeamento inicial ou auto-detectar
      if (initialMapping[key]) {
        newMapping[key] = initialMapping[key];
      } else {
        newMapping[key] = autoDetectField(key, hint);
      }
    });

    setMapping(newMapping);
  }, [template, initialMapping]);

  // Notificar mudanças
  useEffect(() => {
    if (onChange) {
      onChange(mapping);
    }
  }, [mapping, onChange]);

  const handleTypeChange = useCallback((variableKey, type) => {
    setMapping((prev) => ({
      ...prev,
      [variableKey]: {
        type,
        field: type === "lead_field" ? "name" : undefined,
        value: type === "fixed" ? "" : undefined,
      },
    }));
  }, []);

  const handleFieldChange = useCallback((variableKey, field) => {
    setMapping((prev) => ({
      ...prev,
      [variableKey]: {
        ...prev[variableKey],
        type: "lead_field",
        field,
      },
    }));
  }, []);

  const handleValueChange = useCallback((variableKey, value) => {
    setMapping((prev) => ({
      ...prev,
      [variableKey]: {
        ...prev[variableKey],
        type: "fixed",
        value,
      },
    }));
  }, []);

  // Gerar preview com valores de um lead de exemplo
  const getPreviewValue = (variableKey) => {
    const config = mapping[variableKey];
    if (!config) return `{{${variableKey}}}`;

    if (config.type === "fixed") {
      return config.value || `[Valor fixo vazio]`;
    }

    if (config.type === "lead_field") {
      const sampleLead = leads[0];
      if (sampleLead && config.field) {
        return sampleLead[config.field] || `[${config.field}]`;
      }
      const fieldInfo = LEAD_FIELDS.find((f) => f.key === config.field);
      return fieldInfo ? `[${fieldInfo.label}]` : `[Campo]`;
    }

    return `{{${variableKey}}}`;
  };

  if (!template?.variables || template.variables.length === 0) {
    return (
      <div className="template-variable-form-empty">
        Este template não possui variáveis para configurar.
      </div>
    );
  }

  return (
    <div className="template-variable-form">
      <div className="template-variable-form-header">
        <h4>Mapeamento de Variáveis</h4>
        <p className="template-variable-form-hint">
          Configure como cada variável será preenchida para os leads
        </p>
      </div>

      <div className="template-variable-list">
        {template.variables.map((variable, idx) => {
          const key = variable.key || variable;
          const hint = variable.hint || "";
          const config = mapping[key] || { type: "fixed", value: "" };

          return (
            <div key={idx} className="template-variable-row">
              <div className="template-variable-info">
                <code className="template-variable-key">{`{{${key}}}`}</code>
                {hint && <span className="template-variable-hint-text">{hint}</span>}
              </div>

              <div className="template-variable-config">
                <div className="template-variable-type-selector">
                  <label className="template-variable-type-option">
                    <input
                      type="radio"
                      name={`var-type-${key}`}
                      checked={config.type === "lead_field"}
                      onChange={() => handleTypeChange(key, "lead_field")}
                    />
                    <span>Campo do Lead</span>
                  </label>
                  <label className="template-variable-type-option">
                    <input
                      type="radio"
                      name={`var-type-${key}`}
                      checked={config.type === "fixed"}
                      onChange={() => handleTypeChange(key, "fixed")}
                    />
                    <span>Valor Fixo</span>
                  </label>
                </div>

                {config.type === "lead_field" ? (
                  <select
                    value={config.field || ""}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    className="template-variable-select"
                  >
                    <option value="">Selecione o campo</option>
                    {LEAD_FIELDS.map((field) => (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={config.value || ""}
                    onChange={(e) => handleValueChange(key, e.target.value)}
                    placeholder="Digite o valor fixo"
                    className="template-variable-input"
                  />
                )}
              </div>

              {showPreview && (
                <div className="template-variable-preview">
                  <span className="template-variable-preview-label">Preview:</span>
                  <span className="template-variable-preview-value">
                    {getPreviewValue(key)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="template-variable-form-footer">
        <span className="template-variable-auto-hint">
          As variáveis são pré-configuradas automaticamente quando possível
        </span>
      </div>
    </div>
  );
};

export default TemplateVariableForm;
